import { paceBand } from "./pace.js";
const KINDS_FOR = {
    movement: ["wearable.activity"],
    sleep: ["wearable.sleep"],
    stress: ["wearable.hrv", "wearable.stress"],
};
const BEHAVIOUR_FOR_KIND = new Map(Object.entries(KINDS_FOR).flatMap(([behaviour, kinds]) => kinds.map((kind) => [kind, behaviour])));
function worse(t, c) {
    if (t.better === "higher")
        return c.change < 0;
    if (t.better === "lower")
        return c.change > 0;
    return false;
}
/** Behaviours whose week has slipped against the week before, past the member's own variation. */
export function slippingBehaviours(trends) {
    const out = new Set();
    for (const t of trends ?? []) {
        const behaviour = BEHAVIOUR_FOR_KIND.get(t.kind);
        if (behaviour && t.weekOverWeek?.notable && worse(t, t.weekOverWeek))
            out.add(behaviour);
    }
    return [...out];
}
const steps = (n) => (Math.round(n / 100) * 100).toLocaleString("en-GB");
const WEEK_LABELS = {
    "wearable.activity": "Steps this week",
    "wearable.sleep": "Sleep this week",
};
function amount(t, v) {
    if (t.kind === "wearable.activity")
        return `${steps(v)} a day`;
    if (t.unit === "hours")
        return `${v.toFixed(1)} hours a night`;
    return `${Math.round(v)} ${t.unit}`;
}
function plain(t, v) {
    if (t.kind === "wearable.activity")
        return steps(v);
    return t.unit === "hours" ? v.toFixed(1) : String(Math.round(v));
}
/** This week against last, only when it moved past the member's own variation. A clinical metric says which way, never a number. */
export function weekRow(t) {
    const w = t.weekOverWeek;
    if (!w?.notable)
        return null;
    const label = WEEK_LABELS[t.kind] ?? `${t.label.charAt(0).toUpperCase()}${t.label.slice(1)} this week`;
    if (t.clinical) {
        return {
            label,
            value: w.change < 0 ? "Lower than the week before" : "Higher than the week before",
            kind: t.kind,
        };
    }
    const dir = w.change < 0 ? "down" : "up";
    return {
        label,
        value: `${amount(t, w.current)}, ${dir} from ${plain(t, w.previous)} the week before`,
        kind: t.kind,
    };
}
/** The week's row for a pillar's own metric, the first of its kinds that moved. */
export function pillarWeekRow(pillar, body) {
    for (const kind of KINDS_FOR[pillar] ?? []) {
        const t = body.trends?.find((x) => x.kind === kind);
        const row = t ? weekRow(t) : null;
        if (row)
            return row;
    }
    return null;
}
/** Today against yesterday at the same clock time, as a card row, only when it is more than an ordinary gap. */
export function paceRow(body) {
    const p = body.pace;
    if (!p || paceBand(p) === "on_pace")
        return null;
    if (p.unit === "steps") {
        return {
            label: "Steps today",
            value: `${p.today.toLocaleString("en-GB")}, against ${steps(p.yesterday)} by this time yesterday`,
            kind: "wearable.activity",
        };
    }
    return {
        label: "Active today",
        value: `${p.today} min, against ${p.yesterday} min by this time yesterday`,
        kind: "wearable.activity",
    };
}
export const WEEK_BANDS = ["slipping", "steady", "improving", "unknown"];
/** The pillar's own week against the week before, for splitting lift. */
export function weekBand(trends, pillar) {
    const t = (KINDS_FOR[pillar] ?? [])
        .map((kind) => trends?.find((x) => x.kind === kind))
        .find((x) => x?.weekOverWeek);
    const w = t?.weekOverWeek;
    if (!t || !w || t.better === null)
        return "unknown";
    if (!w.notable)
        return "steady";
    return worse(t, w) ? "slipping" : "improving";
}
//# sourceMappingURL=trend-reads.js.map